"use client";

/**
 * CoachAudioControls — volume + device pickers for a Listen/Whisper session.
 *
 * ONE component used by BOTH surfaces (the Live Calls monitor rail and the
 * floating widget) so they can't drift apart. Everything reads from
 * CoachSessionContext, which owns the session, so a change made in the widget is
 * instantly reflected on the console and vice-versa.
 *
 * Device lists come from TwilioDeviceContext, which already enumerates them for
 * the dialer — no second permission prompt, no duplicate enumeration.
 *
 * The mic row only renders in whisper: in listen mode the supervisor is muted by
 * design, so offering a microphone picker would imply they can be heard.
 */

import React, { useEffect, useState } from "react";
import { Volume2, Mic, Headphones, RefreshCw, Volume1 } from "lucide-react";
import { useCoachSession } from "@/app/_module/contexts/CoachSessionContext";
import { useTwilioDeviceContext } from "@/app/_module/contexts/TwilioDeviceContext";
import {
  supportsSpeakerphone,
  setSpeakerphone,
  isSpeakerphoneOn,
} from "@/lib/capacitor/audio-session";

interface Props {
  /** Show the microphone picker (whisper only). */
  showMic: boolean;
  /** Tighter paddings for the floating widget. */
  compact?: boolean;
}

export default function CoachAudioControls({ showMic, compact }: Props) {
  const {
    volume, setVolume,
    outputDeviceId, setOutputDeviceId,
    inputDeviceId, setInputDeviceId,
    outputRoutingSupported,
  } = useCoachSession();

  const {
    audioInputDevices,
    audioOutputDevices,
    refreshAudioDevices,
  } = useTwilioDeviceContext();

  // Populate the lists on mount. Labels are empty until mic permission has been
  // granted at least once; whisper triggers that, and the dialer usually has
  // already done it.
  useEffect(() => {
    refreshAudioDevices?.().catch(() => { /* non-fatal */ });
  }, [refreshAudioDevices]);

  // Native app: WebKit has no setSinkId, so the output picker is inert there.
  // Only AVAudioSession can move the route — offer the same speakerphone
  // toggle the dialer uses instead of a dead dropdown.
  const nativeSpeaker = supportsSpeakerphone();
  const [speakerOn, setSpeakerOn] = useState(false);
  useEffect(() => {
    if (!nativeSpeaker) return;
    isSpeakerphoneOn().then(setSpeakerOn).catch(() => {});
  }, [nativeSpeaker]);
  const toggleSpeaker = async () => {
    const next = !speakerOn;
    if (await setSpeakerphone(next)) setSpeakerOn(next);
  };

  const pad = compact ? "px-2 py-1.5" : "px-2.5 py-2";
  const selectCls =
    "min-w-0 flex-1 truncate rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] text-gray-700 " +
    "focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15";

  const label = (d: { deviceId: string; label?: string }, i: number, kind: string) =>
    d.label && d.label.trim().length > 0 ? d.label : `${kind} ${i + 1}`;

  return (
    <div className={`w-full space-y-1.5 rounded-md bg-gray-50 ${pad} ring-1 ring-inset ring-gray-200/70`}>
      {/* Volume — the one control that must always be here. Applies to the gain
          node live, so dragging it changes what you hear immediately. */}
      <div className="flex items-center gap-2">
        <Volume2 className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        <input
          type="range"
          min={0}
          max={100}
          value={volume}
          onChange={(e) => setVolume(Number(e.target.value))}
          aria-label="Monitor volume"
          className="h-1.5 flex-1 appearance-none rounded-full bg-gray-200 accent-primary"
        />
        <span className="w-8 shrink-0 text-right font-mono text-[11px] tabular-nums text-gray-500">
          {volume}%
        </span>
      </div>

      {nativeSpeaker ? (
        /* Native app: earpiece ↔ speaker toggle (the only routing iOS allows) */
        <div className="flex items-center gap-2">
          <Headphones className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <button
            type="button"
            onClick={toggleSpeaker}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded border px-2 py-1 text-[11px] font-medium transition-colors ${
              speakerOn
                ? "border-primary/30 bg-primary/10 text-primary"
                : "border-gray-200 bg-white text-gray-600"
            }`}
          >
            <Volume1 className="h-3 w-3" />
            {speakerOn ? "Speaker On" : "Speaker Off"}
          </button>
        </div>
      ) : (
        <>
          {/* Output (headphones) */}
          <div className="flex items-center gap-2">
            <Headphones className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            <select
              className={selectCls}
              value={outputDeviceId ?? ""}
              onChange={(e) => setOutputDeviceId(e.target.value || null)}
              aria-label="Output device"
            >
              <option value="">System default</option>
              {audioOutputDevices?.map((d: any, i: number) => (
                <option key={d.deviceId} value={d.deviceId}>{label(d, i, "Output")}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => refreshAudioDevices?.().catch(() => {})}
              title="Refresh device list"
              className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200/60 hover:text-gray-600"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>

          {!outputRoutingSupported && (
            <p className="text-[10px] leading-tight text-amber-600">
              This browser can&apos;t route audio to a specific device — using the system default.
            </p>
          )}
        </>
      )}

      {/* Mic — whisper only (in listen mode you are muted by design) */}
      {showMic && (
        <div className="flex items-center gap-2">
          <Mic className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <select
            className={selectCls}
            value={inputDeviceId ?? ""}
            onChange={(e) => setInputDeviceId(e.target.value || null)}
            aria-label="Microphone"
          >
            <option value="">System default</option>
            {audioInputDevices?.map((d: any, i: number) => (
              <option key={d.deviceId} value={d.deviceId}>{label(d, i, "Mic")}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
